<template>
  <MachineWiseChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :loading="loading"
    title="Last 7 Days (MINS)"
    :daterange="daterange"
  />
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import MachineWiseChart from '../MachineWiseChart.vue';
import {
  buildDowntimeDepartmentDisplayOrder,
  getDowntimeCategoryFromRow,
  getDowntimeCategoryColor,
  getDowntimeDepartmentValue,
  getDowntimeRowValue,
} from '../../utils/downtime-colors';

export default {
  name: 'MachineDowntimeWeek',
  props: {
    widgettitle: { type: String },
    scale: { type: Number, default: 1 },
    unit: { type: String },
  },
  components: { MachineWiseChart },
  data() {
    return { loading: false, daterange: '', currentshifttime: null };
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', [
      'shifts', 'serverTime', 'dataFromJSON', 'config', 'machines', 'downtimeReasons', 'currentDate', 'theme',
    ]),
    ...mapGetters('cellwiseDowntimeDashboard', ['firstShift']),
    cellname() { return this.$route.query.selectedcell || this.$route.query.cellname; },
    chunkSize() { return (this.config && this.config.last7DaysWidgetSlideItems) || 8; },
  },
  watch: {
    cellname() { this.fetchData(); },
    currentDate(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.dataFromJSON) this.fetchData();
    },
    'theme.isDark': function themeIsDark() {
      if (this.dataFromJSON) this.dummydata();
      else this.fetchData();
    },
  },
  mounted() {
    if (!this.dataFromJSON) this.fetchData();
  },
  methods: {
    ...mapActions('cellwiseDowntimeDashboard', [
      'allShifts', 'getReports', 'getMachines', 'fetchDowntimeReasons',
    ]),

    getSiteId() { return this.$store.getters['cellwiseDowntimeDashboard/activeSite']; },

    parseBusinessDate(yyyymmdd) {
      const dateStr = yyyymmdd.toString();
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1;
      const day = parseInt(dateStr.substring(6, 8), 10);
      return new Date(year, month, day);
    },

    getYYYYMMDD(dateObj) {
      return parseInt(
        `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`,
        10,
      );
    },

    getDateLabel(dateObj) {
      return `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    },

    getRowDateKey(row) {
      const possibleDate = row.date || row.Date || row.reportdate || row.reportDate
        || row.report_date
        || row.shiftdate || row.shiftDate || row.businessdate || row.businessDate
        || row.shift_date || row.business_date
        || row.productiondate || row.productionDate || row.production_date
        || row.startdate || row.startDate || row.start_date
        || row.createddate || row.createdDate || row.day || row.dateval || row.dateVal;
      if (!possibleDate) return null;

      const rawValue = possibleDate.toString();
      if (/^\d{8}$/.test(rawValue)) return rawValue;

      if (/^\d{13}$/.test(rawValue)) return this.getYYYYMMDD(new Date(Number(rawValue))).toString();
      if (/^\d{10}$/.test(rawValue)) return this.getYYYYMMDD(new Date(Number(rawValue) * 1000)).toString();

      const dateParts = rawValue.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
      if (dateParts) return `${dateParts[3]}${dateParts[2]}${dateParts[1]}`;

      const compactDateParts = rawValue.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
      if (compactDateParts) return `${compactDateParts[1]}${compactDateParts[2]}${compactDateParts[3]}`;

      const parsedDate = new Date(possibleDate);
      if (Number.isNaN(parsedDate.getTime())) return null;
      return this.getYYYYMMDD(parsedDate).toString();
    },

    getWeekDays(startDateObj) {
      const days = [];
      for (let i = 0; i < 7; i += 1) {
        const dateObj = new Date(startDateObj.getTime());
        dateObj.setDate(dateObj.getDate() + i);
        days.push({
          key: this.getYYYYMMDD(dateObj).toString(),
          label: this.getDateLabel(dateObj),
        });
      }
      return days;
    },

    normalizeText(value) {
      return (value || '').toString().trim().toLowerCase();
    },

    parseReportResult(result) {
      if (!result) return { rows: [], chartOptions: [] };

      let parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch (e) { console.warn(e); }
      }

      if (Array.isArray(parsed)) return { rows: parsed, chartOptions: [] };
      if (parsed && Array.isArray(parsed.reportData)) {
        return { rows: parsed.reportData, chartOptions: [] };
      }
      if (parsed && Array.isArray(parsed.data)) return { rows: parsed.data, chartOptions: [] };
      if (parsed && parsed.reportData && Array.isArray(parsed.reportData.chartOptions)) {
        return { rows: [], chartOptions: parsed.reportData.chartOptions };
      }
      if (parsed && Array.isArray(parsed.chartOptions)) {
        return { rows: [], chartOptions: parsed.chartOptions };
      }
      return { rows: [], chartOptions: [] };
    },

    getRowDowntimeValue(row) {
      return getDowntimeRowValue(row);
    },

    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        if (!this.currentDate) {
          this.setZeroState();
          return;
        }

        const endDateObj = this.parseBusinessDate(this.currentDate);
        endDateObj.setDate(endDateObj.getDate() - 1);

        const startDateObj = new Date(endDateObj.getTime());
        startDateObj.setDate(startDateObj.getDate() - 6);

        this.setTitle(startDateObj.getTime(), endDateObj.getTime());

        const activeSite = this.getSiteId();
        if (!activeSite) {
          this.setZeroState();
          return;
        }

        if (!this.machines || this.machines.length === 0) await this.getMachines();
        let validMachineNames = [];

        if (this.machines && this.machines.length > 0) {
          if (this.cellname) {
            validMachineNames = this.machines
              .filter((m) => this.normalizeText(m.machinecell)
               === this.normalizeText(this.cellname))
              .map((m) => m.machinename);
          } else {
            validMachineNames = this.machines.map((m) => m.machinename);
          }
        }

        if (validMachineNames.length === 0) {
          this.setZeroState();
          return;
        }

        const validMachineLookup = validMachineNames.reduce((acc, machineName) => {
          acc[this.normalizeText(machineName)] = true;
          return acc;
        }, {});

        const reportName = 'dailymachinedowntime';
        const days = this.getWeekDays(startDateObj);

        let actualArray = [];
        let prebuiltChartOptions = [];

        const dayResults = await Promise.all(days.map(async (day) => {
          const payload = {
            start: Number(day.key),
            end: Number(day.key),
            siteid: activeSite,
          };
          const dayResult = await this.getReports({ reportName, payload });
          const parsedDayResult = this.parseReportResult(dayResult);

          if (parsedDayResult.chartOptions.length > 0) {
            prebuiltChartOptions = parsedDayResult.chartOptions;
          }

          return parsedDayResult.rows.map((row) => ({
            ...row,
            downtimeDateKey: day.key,
          }));
        }));

        actualArray = dayResults.reduce((rows, dayRows) => rows.concat(dayRows), []);

        if (actualArray.length <= 0 && prebuiltChartOptions.length > 0) {
          const paginatedResult = this.sliceIntoChunks(prebuiltChartOptions, this.chunkSize);
          if (this.$refs.chart && paginatedResult.length > 0) {
            this.$refs.chart.assignData(paginatedResult);
          } else this.setZeroState();
          return;
        }

        actualArray = actualArray.filter((row) => {
          const mName = row.machinename || row.machine || row.assetname || '';
          return validMachineLookup[this.normalizeText(mName)];
        });

        if (!this.downtimeReasons || this.downtimeReasons.length === 0) {
          if (typeof this.fetchDowntimeReasons === 'function') {
            try { await this.fetchDowntimeReasons(); } catch (e) { /* Ignore */ }
          }
        }

        const dayLookup = days.reduce((acc, day) => {
          acc[day.key] = day;
          return acc;
        }, {});

        const machineMap = {};
        const safeReasons = Array.isArray(this.downtimeReasons) ? this.downtimeReasons : [];
        const activeTheme = this.$vuetify.theme.dark ? 'dark' : 'light';

        // PRE-FILL MAP: Guarantees every machine renders an axis, even if 0 data!
        validMachineNames.forEach((mName) => {
          machineMap[mName] = {};
        });

        actualArray.forEach((row) => {
          const mName = row.machinename || row.machine || row.assetname || 'Unknown';
          // Strictly use the daily tag we injected in the Promise.all loop
          const dayKey = row.downtimeDateKey;
          if (!dayKey || !dayLookup[dayKey]) return;

          const cat = getDowntimeCategoryFromRow(row, safeReasons, 'other');
          const dTime = this.getRowDowntimeValue(row);

          if (!machineMap[mName]) machineMap[mName] = {};
          if (!machineMap[mName][cat]) machineMap[mName][cat] = {};
          machineMap[mName][cat][dayKey] = (Number(machineMap[mName][cat][dayKey])
           || 0) + (Number(dTime) || 0);
        });

        const visibleDepartments = buildDowntimeDepartmentDisplayOrder(
          actualArray,
          safeReasons,
          6,
        );
        const departmentOrder = visibleDepartments;

        const chartOptions = Object.keys(machineMap).sort().map((mName) => {
          const series = visibleDepartments.map((cat) => {
            const dayValues = getDowntimeDepartmentValue(machineMap[mName], cat) || {};

            return {
              name: cat,
              data: days.map((day) => {
                const totalMins = Number(dayValues[day.key]) || 0;
                const totalSeconds = Math.round(totalMins * 60);
                const hrs = Math.floor(totalSeconds / 3600);
                const mns = Math.floor((totalSeconds % 3600) / 60);
                const scs = totalSeconds % 60;
                const formattedTime = `${hrs.toString().padStart(2, '0')}:${mns.toString()
                  .padStart(2, '0')}:${scs.toString().padStart(2, '0')}`;

                return {
                  y: Number(totalMins.toFixed(2)),
                  customTime: formattedTime,
                };
              }),
              color: getDowntimeCategoryColor(
                this.config,
                cat,
                activeTheme,
                departmentOrder,
              ),
            };
          });

          return {
            machinename: mName,
            id: mName,
            options: {
              chart: {
                type: 'column',
                backgroundColor: 'transparent',
                marginTop: 8,
                marginRight: 8,
                marginBottom: 20,
                spacing: [0, 0, 0, 0],
              },
              title: { text: null },
              xAxis: {
                categories: days.map((day) => day.label),
                lineColor: '#AEB6C2',
                tickColor: '#AEB6C2',
                labels: { enabled: true },
              },
              yAxis: {
                min: 0,
                title: { text: null },
                labels: { enabled: true },
                gridLineWidth: 1,
              },
              legend: { enabled: false },
              tooltip: { pointFormat: '{series.name}: <b>{point.customTime}</b>' },
              plotOptions: {
                series: {
                  stacking: 'normal',
                  borderWidth: 0,
                  pointPadding: 0.1,
                  groupPadding: 0.1,
                  dataLabels: {},
                },
              },
              series,
              credits: { enabled: false },
            },
          };
        });

        const paginatedResult = this.sliceIntoChunks(chartOptions, this.chunkSize);
        if (this.$refs.chart && paginatedResult.length > 0) {
          this.$refs.chart.assignData(paginatedResult);
        } else this.setZeroState();
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },

    setZeroState() {
      if (this.$refs.chart) this.$refs.chart.assignData([]);
    },

    setTitle(start, end) {
      if (start && end && start !== end) this.daterange = `${this.dateFormat(start)} - ${this.dateFormat(end)}`;
      else this.daterange = this.dateFormat(start);
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) { return ''; }
    },

    sliceIntoChunks(arr, chunkSize) {
      const res = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        res.push(chunk);
      }
      return res;
    },
  },
};
</script>
